import { useState, useRef } from "react";
import { useCommunity } from "@contexts/CommunityContext";

import { Text } from "@fragments";
import { BackupRounded } from "@mui/icons-material";

import CustomModal from "@components/CustomModal";

import { AssetCategory, AssetType, FileDropAssetField } from "@features/uploadAsset";

import General from "./components/General";
import Availability from "./components/Availability";

import { createMaterial, updateMaterial, deleteMaterial } from "./api";
import { CreateMaterialDTO, UpdateMaterialDTO, DeleteMaterialDTO, GetMaterialDTO } from "./dto";
import { FileValidation } from "./enums";

export default function ManageMaterialModal({ modalRef }) {
  const REF_config = useRef({});
  const REF_callback = useRef();
  const REF_community = useRef({});

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorTitle, setErrorTitle] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [activeCommunityId, setActiveCommunityId] = useState(null);
  const [uploadBusyCount, setUploadBusyCount] = useState(0);
  const [, setMaterialFileTick] = useState(0);

  const { community } = useCommunity();

  const bumpMaterialFile = () => setMaterialFileTick((n) => n + 1);

  const handleUploadingChange = (uploading) => {
    setUploadBusyCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
  };

  modalRef.current = {
    open(preFill, callback) {
      console.log('open edit', preFill);

      const materialDTO = new GetMaterialDTO(preFill);
      REF_config.current = materialDTO.toObject();

      REF_community.current = {
        ...community.get(preFill.community?.id || preFill.community)
      };

      setActiveCommunityId(materialDTO.getCommunityId());
      REF_callback.current = callback;
      setOpen(true);
      setErrorTitle(null);
      setErrorMessage(null);
    },
  };

  async function handleSubmit() {
    try {
      // Determine if this is a create or update operation
      const isUpdate = !!REF_config.current.id;

      // Create the appropriate DTO
      const DTO = isUpdate
        ? new UpdateMaterialDTO(REF_config.current)
        : new CreateMaterialDTO(REF_config.current);

      // Validate the DTO
      const validation = DTO.validate();
      if (!validation.valid) {
        setErrorTitle('Validação necessária');
        setErrorMessage(validation.error);
        return;
      }

      setLoading(true);

      // Call the appropriate API handler
      const result = isUpdate
        ? await updateMaterial(DTO)
        : await createMaterial(DTO);

      if (result.success) {
        await REF_callback.current(result.data);
        setOpen(false);
        setErrorTitle(null);
        setErrorMessage(null);
      } else {
        setErrorTitle('Erro ao salvar o material');
        setErrorMessage(result.error);
      }
    } catch (error) {
      console.error('Unexpected error in handleSubmit:', error);
      setErrorTitle('Erro inesperado');
      setErrorMessage('Um erro inesperado ocorreu. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    try {
      const deleteDTO = new DeleteMaterialDTO(REF_config.current.id);

      // Validate the DTO
      const validation = deleteDTO.validate();
      if (!validation.valid) {
        setErrorTitle('Erro ao deletar');
        setErrorMessage(validation.error);
        return;
      }

      setLoading(true);

      const result = await deleteMaterial(deleteDTO.getId());

      if (result.success) {
        await REF_callback.current();
        setOpen(false);
        setErrorTitle(null);
        setErrorMessage(null);
        window.navigate(-1);
      } else {
        setErrorTitle('Erro ao deletar o material');
        setErrorMessage(result.error);
      }
    } catch (error) {
      console.error('Unexpected error in handleDelete:', error);
      setErrorTitle('Erro inesperado');
      setErrorMessage('Um erro inesperado ocorreu ao deletar. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }

  function handleAvailabilityChange(result) {
    if (result === null) {
      REF_config.current.availability = null;
    } else if (Array.isArray(result)) {
      REF_config.current.availability = [...result];
    } else {
      REF_config.current.availability = result;
    }
  }

  const assetUploading = uploadBusyCount > 0;

  return (
    <CustomModal
      maxWidth={550}
      bottomButtonSumbitText="Salvar material"
      bottomButtonCancelText="Cancelar"
      loading={loading}
      disabled={assetUploading}
      handleDelete={REF_config.current?.id && handleDelete}
      deletionTitle="Você realmente deseja apagar este material permanentemente?"
      title={REF_config.current?.id ? "Editar material" : "Criar novo material"}
      handleSubmit={handleSubmit}
      open={open}
      handleClose={() => setOpen(false)}
      errorTitle={errorTitle}
      errorMessage={errorMessage}
      bodyComponent={
        open ? (
          <div className="px-4 py-1">
            <Text.h3
              icon={<BackupRounded />}
              className="mt-3"
              value="Arquivo do material (Formato PDF)"
            />

            <FileDropAssetField
              assetType={AssetType.PDF}
              communityId={activeCommunityId}
              category={AssetCategory.MATERIAL_CONTENT}
              path={REF_config.current.path}
              onPathChange={(url) => {
                REF_config.current.path = url;
                bumpMaterialFile();
              }}
              onUploadingChange={handleUploadingChange}
              maxBytes={FileValidation.MAX_FILE_SIZE}
            />

            <General
              configRef={REF_config}
              communityId={activeCommunityId}
              onUploadingChange={handleUploadingChange}
            />

            <Availability
              configRef={REF_config}
              onAvailabilityChange={handleAvailabilityChange}
            />
          </div>
        ) : null
      }
    />
  );
}
